/**
 * Platform Adapter — runtime/environment detection (#1690, spec.md §2.1/§2.2,
 * addendum §5.1/§5.2). This is the ONE place in the app allowed to ask
 * "are we running inside the native (Capacitor) WebView, or a real browser?" —
 * every other module must call into here, not re-derive the answer itself.
 *
 * Reads `window.Capacitor` directly rather than importing `@capacitor/core`:
 * the native runtime injects this global into the WebView before any app code
 * runs, and it is Capacitor's own public/documented contract — the npm
 * package's `Capacitor.isNativePlatform()` is a thin wrapper over the same
 * object. This file is imported from hot paths used by every environment
 * (firebase/app.js, autoNumber.js, index.js…), so it deliberately carries no
 * dependency that could fail to resolve and take the whole app down with it —
 * a missing or mis-shaped `window.Capacitor` degrades to 'web', it never
 * throws.
 */

export function getPlatform() {
  if (typeof window === 'undefined' || !window.Capacitor) {
    return 'web'
  }
  try {
    if (typeof window.Capacitor.getPlatform === 'function') {
      return window.Capacitor.getPlatform() || 'web'
    }
    return window.Capacitor.platform || 'web'
  } catch {
    return 'web'
  }
}

export function isNativePlatform() {
  return getPlatform() !== 'web'
}

/**
 * The only place allowed to answer "is this a developer's own machine,
 * talking to the local Firebase emulator, rather than a real deployment?"
 *
 * `window.location.hostname === 'localhost'` used to answer this directly in
 * several places (firebase/app.js, autoNumber.js, Login.js, Notifications.js)
 * — and that check is wrong inside a native WebView. Capacitor's default
 * scheme is `capacitor://localhost` on iOS and historically `http://localhost`
 * on Android, so the hostname is LITERALLY "localhost" on every real native
 * launch. Code that trusted hostname alone would treat every production
 * native app run as local development (spec addendum §5.1) — exactly the trap
 * the spec calls out by name. Gating on `isNativePlatform()` first is what
 * prevents that.
 */
export function isLocalWebDevelopmentHost() {
  if (isNativePlatform()) {
    return false
  }
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  return ['localhost', '127.0.0.1'].includes(window.location.hostname)
}
